import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    type CarouselOptions,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-chevron-right.svg';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { RecentlyViewedProductTile } from './RecentlyViewedProductTile';

interface ProductsPageRecentlyViewedProps {
    items: ProductTileViewModel[];
}

/**
 * Recently Viewed section specifically for the Products page (desktop only).
 * Features decorative line above title, simplified tiles, and circular navigation arrows.
 * Matches Figma design node 20625:2454.
 */
export const ProductsPageRecentlyViewed = ({
    items,
}: ProductsPageRecentlyViewedProps) => {
    if (items.length === 0) {
        return null;
    }

    const carouselOptions: CarouselOptions = {
        slidesToScroll: 1,
        align: 'start',
        dragFree: true,
        breakpoints: {
            '(min-width: 768px)': {
                slidesToScroll: 4,
            },
        },
    };

    return (
        <section
            className="pb-[5.625rem]"
            aria-labelledby="recently-viewed-heading"
        >
            <Carousel options={carouselOptions}>
                {/* Header with decorative line */}
                <div className="px-6 pb-[3.125rem] md:px-12.5">
                    <hr className="m-0 h-0 border-t border-[#e5e5e5]" />
                    <div className="flex items-center justify-between pt-5">
                        <h2
                            id="recently-viewed-heading"
                            className="text-5.5 font-medium leading-normal tracking-[0.22px] text-gray-900"
                        >
                            Recently Viewed
                        </h2>

                        {/* Circular navigation arrows */}
                        <div className="flex gap-[0.417rem]">
                            <CarouselPrevious
                                color="secondary"
                                className="!bg-[#f2f2f2]"
                                aria-label="View previous recently viewed products"
                            >
                                <ArrowLeft className="size-4.5" />
                            </CarouselPrevious>
                            <CarouselNext
                                color="secondary"
                                className="!bg-[#f2f2f2]"
                                aria-label="View next recently viewed products"
                            >
                                <ArrowRight className="size-4.5" />
                            </CarouselNext>
                        </div>
                    </div>
                </div>

                {/* Product tiles carousel */}
                <CarouselContent>
                    {items.map((product) => (
                        <CarouselItem
                            key={`recently-viewed-${product.id}`}
                            className="mr-4 max-w-[21.375rem] first:ml-6 last:mr-6 md:first:ml-12.5 md:last:mr-12.5"
                        >
                            <RecentlyViewedProductTile product={product} />
                        </CarouselItem>
                    ))}
                </CarouselContent>
            </Carousel>
        </section>
    );
};
